import React from 'react';

interface Props {
  children: React.ReactNode;
  // Changing this value resets the boundary (e.g. pass the route path so
  // navigating away from a crashed page recovers automatically).
  resetKey?: string;
}
interface State { error: Error | null; }

/**
 * Catches render/lifecycle errors from the page it wraps and shows a readable
 * error card instead of letting React unmount the whole app to a blank screen.
 * The error message is shown on purpose — it turns "blank window" bug reports
 * into actionable ones.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surfaces in the browser console with the component stack for debugging.
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="max-w-2xl mx-auto mt-10 bg-white border border-red-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">⚠️</span>
            <h2 className="text-lg font-semibold text-gray-900">Something went wrong on this page</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            The rest of the app is fine — you can go back or reload. If this keeps
            happening, please share the details below with support.
          </p>
          <pre className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
            {this.state.error.message || String(this.state.error)}
          </pre>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => this.setState({ error: null })}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
